import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys } from 'noddi-async';
import type { NoddiColumnDef, NoddiPaginationState, NoddiTableTranslations, TableAction } from 'noddi-ui-web';
import { ApiErrorPageInternal, NoddiTable } from 'noddi-ui-web';
import type { CarGroup, MemberGroup } from './notificationGroupTypes';
import { useNotificationOverviewMembers } from './useNotificationOverviewMembers';

type TableProps<T> = {
  userGroupId: number;
  /**
   * Owned by the overview: it resets the page when its attention filter changes, and
   * reads the member sheet's row back from the page the table shows.
   */
  pagination: NoddiPaginationState;
  setPagination: (pagination: NoddiPaginationState) => void;
  columns: NoddiColumnDef<T>[];
  actions: TableAction<T>[];
  onRowClick: (row: T) => void;
  searchPlaceholder: string;
  translations?: NoddiTableTranslations;
  showCopyRowId: boolean;
};

export const NotificationCarsTable = ({
  userGroupId,
  pagination,
  setPagination,
  needsAttention,
  columns,
  actions,
  onRowClick,
  searchPlaceholder,
  translations,
  showCopyRowId
}: TableProps<CarGroup> & { needsAttention: boolean }) => {
  const { data, isPending, isFetching, error } = noddiAsync.useGet({
    type: URLKeys.getPaginatedNotificationOverviewCars,
    input: { userGroupId, ...pagination, needsAttention: needsAttention || undefined },
    queryConfig: { placeholderData: keepPreviousData }
  });

  if (error) {
    return <ApiErrorPageInternal error={error} />;
  }
  return (
    <NoddiTable
      data={data?.results}
      totalCount={data?.count}
      columns={columns}
      pagination={pagination}
      setPagination={setPagination}
      isPending={isPending}
      isFetching={isFetching}
      getRowId={(row) => row.carId}
      actions={actions}
      onRowClick={onRowClick}
      searchPlaceholder={searchPlaceholder}
      translations={translations}
      hideCopyRowId={!showCopyRowId}
    />
  );
};

export const NotificationMembersTable = ({
  userGroupId,
  pagination,
  setPagination,
  columns,
  actions,
  onRowClick,
  searchPlaceholder,
  translations,
  showCopyRowId
}: TableProps<MemberGroup>) => {
  const { data, isPending, isFetching, error } = useNotificationOverviewMembers(userGroupId, pagination);

  if (error) {
    return <ApiErrorPageInternal error={error} />;
  }
  return (
    <NoddiTable
      data={data?.results}
      totalCount={data?.count}
      columns={columns}
      pagination={pagination}
      setPagination={setPagination}
      isPending={isPending}
      isFetching={isFetching}
      getRowId={(row) => row.userGroupMemberId}
      actions={actions}
      onRowClick={onRowClick}
      searchPlaceholder={searchPlaceholder}
      translations={translations}
      hideCopyRowId={!showCopyRowId}
    />
  );
};
